import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Search, UserRoundCog } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { useCustomerSearch } from '@/features/customers/customers.api';
import type { Customer } from '@/features/customers/types';
import { post } from '@/lib/api/client';
import { formatPhone, initials } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useAuthStore } from './auth.store';
import type { LoginResponse } from './types';

/**
 * Sign in as somebody else, to see what they see.
 *
 * The swap is real: the admin's token is replaced by one issued for the other
 * account, so every request afterwards is made as them. The admin's own session
 * is parked in the auth store first — see `startImpersonation` — and the banner
 * in the shell is what puts it back.
 *
 * The original did all of that by writing four localStorage keys by hand from
 * inside the page component, which meant the store and storage could disagree,
 * and put the way out below the footer.
 */
export function ImpersonationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  const [target, setTarget] = useState<Customer | null>(null);

  const search = useCustomerSearch(term);
  const start = useAuthStore((state) => state.startImpersonation);

  const impersonate = useMutation({
    mutationFn: (userId: string) =>
      post<LoginResponse>('oauth/impersonate', { UserId: userId }),
  });

  const results = search.data?.results ?? [];

  function go(user: Customer) {
    impersonate.mutate(user.id, {
      onSuccess: (session) => {
        start(session);
        // Nothing cached was fetched as this person.
        queryClient.clear();
        setTarget(null);
        toast.success(
          t('impersonation.started', {
            name:
              [user.firstName, user.lastName].filter(Boolean).join(' ') ||
              user.email ||
              '',
          })
        );
        // The admin pages are gone now — this one included — so staying put
        // would land on the unauthorised screen.
        navigate('/');
      },
      onError: (error) =>
        toast.error(
          error instanceof Error ? error.message : t('impersonation.failed')
        ),
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('impersonation.title')}
        description={t('impersonation.subtitle')}
      />

      <div className="border-warning/40 bg-warning/10 flex items-start gap-2 rounded-xl border p-4 text-sm">
        <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" />
        <div className="space-y-1">
          <p className="font-medium">{t('impersonation.warningTitle')}</p>
          <p className="text-muted-foreground text-pretty">
            {t('impersonation.warningBody')}
          </p>
        </div>
      </div>

      <form
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          setTerm(draft.trim());
        }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('impersonation.searchPlaceholder')}
            aria-label={t('impersonation.searchPlaceholder')}
            autoFocus
            className="h-10 pl-9"
          />
        </div>
        <Button type="submit" className="h-10" disabled={!draft.trim()}>
          <span className="hidden sm:inline">{t('common.search')}</span>
          <Search className="sm:hidden" />
        </Button>
      </form>

      {!term && (
        <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
          {t('impersonation.startBody')}
        </p>
      )}

      {term && search.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-9 shrink-0 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-56" />
              </div>
            </div>
          ))}
        </div>
      )}

      {term && search.isSuccess && results.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('impersonation.noResults', { term })}
          </p>
        </div>
      )}

      {results.length > 0 && (
        <div className="relative rounded-xl">
          {/* A background refetch of the same term keeps the rows on screen. */}
          <BusyOverlay show={search.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              search.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {results.map((user) => (
              <div key={user.id} className="flex items-center gap-3 p-3">
                <Avatar className="size-9 shrink-0">
                  {user.avatar && <AvatarImage src={user.avatar} alt="" />}
                  <AvatarFallback>
                    {initials(user.firstName, user.lastName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
                      '—'}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {user.email}
                    {user.phoneNumber && ` · ${formatPhone(user.phoneNumber)}`}
                  </p>
                </div>
                {user.deletedAt && (
                  <Badge variant="destructive">
                    {t('impersonation.deleted')}
                  </Badge>
                )}
                <Button
                  size="sm"
                  disabled={impersonate.isPending || !!user.deletedAt}
                  onClick={() => setTarget(user)}
                >
                  <UserRoundCog className="size-3.5" />
                  {t('impersonation.signInAs')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <AlertDialog
        open={target !== null}
        onOpenChange={(next) => {
          if (!next) setTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('impersonation.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('impersonation.confirmBody', {
                name:
                  [target?.firstName, target?.lastName]
                    .filter(Boolean)
                    .join(' ') ||
                  target?.email ||
                  '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={impersonate.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={impersonate.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (target) go(target);
              }}
            >
              {impersonate.isPending && <Loader2 className="animate-spin" />}
              {t('impersonation.signInAs')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
